import React, { ReactElement, useEffect, useState } from "react";
import { Button, Grid, TextField, Toolbar, Typography } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DEFAULT_DATE_FORMAT } from "shared/constants";
import { useGet, usePut } from "shared/react-query/hooks";
import {
  AUDIT_TRAIL_SYNCS_START,
  AUDIT_TRAIL_SYNCS_STOP,
  GET_AUDIT_TRAIL_SYNCS,
} from "back-office/constants";
import { useStateMachine } from "little-state-machine";
import { BackOfficeKeys } from "shared/react-query/types";
import AuditTrailSyncTable from "back-office/components/AuditTrailSyncTable";
import { logDateSchema } from "./config";

export const AuditTrailSyncPage = (): ReactElement => {
  const [logDate, setLogDate] = useState<Date | null>(new Date());
  const [isRetrieveBtnEnabled, setIsRetrieveBtnEnabled] =
    useState<boolean>(true);

  const {
    state: { profile },
  } = useStateMachine();
  const { agencyId, userId, userAgency } = profile;

  // Get auditTrailSyncData
  const { data: auditTrailSyncData, setInitialParams: setAuditTrailSyncDate } =
    useGet(GET_AUDIT_TRAIL_SYNCS, BackOfficeKeys.AUDIT_TRAIL_SYNC, {
      initialParams: { logDate: logDate?.toISOString() },
    });
  const { items = [], isRunning } = auditTrailSyncData ?? {};

  // Methods for starting and stoping background job
  const startJob = usePut(AUDIT_TRAIL_SYNCS_START);
  const stopJob = usePut(AUDIT_TRAIL_SYNCS_STOP);

  useEffect(() => {
    setIsRetrieveBtnEnabled(logDateSchema.isValidSync(logDate));
  }, [logDate, setIsRetrieveBtnEnabled]);

  const changeIsBackgroundJobRunning = async () => {
    const body = { agencyId, userId, userAgency };
    if (!isRunning) {
      startJob.mutate(body);
    } else {
      stopJob.mutate(body);
    }

    setLogDate(new Date());
    setAuditTrailSyncDate({ logDate: new Date().toISOString() });
  };

  return (
    <>
      <Toolbar>
        <Grid alignItems="center" container spacing={2}>
          <Grid item xs={2}>
            <Grid item>
              <Typography>
                Job status: {isRunning ? "Running" : "Not Running"}
              </Typography>
            </Grid>
          </Grid>
          <Grid item xs={2}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Due date"
                inputFormat={DEFAULT_DATE_FORMAT}
                mask="__-__-____"
                maxDate={new Date()}
                value={logDate}
                onChange={(e: Date | null) => {
                  if (e?.toString() !== "Invalid Date") {
                    e?.setTime(e?.getTime() + 2 * 60 * 60 * 1000); // TODO: temp solution requires refactorisation of handling dates.
                    setLogDate(e);
                  }
                }}
                renderInput={(params) => <TextField {...params} />}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={2}>
            <Button
              id="button-retrieveLogs"
              size="small"
              variant="contained"
              onClick={() =>
                setAuditTrailSyncDate({ logDate: logDate?.toISOString() })
              }
              disabled={!isRetrieveBtnEnabled}
            >
              Retrieve Logs
            </Button>
          </Grid>
          <Grid item xs={6}>
            <Grid container justifyContent="flex-end" spacing={2}>
              <Grid item>
                <Button
                  id="button-stopJob"
                  color="secondary"
                  variant="contained"
                  disabled={!isRunning}
                  onClick={changeIsBackgroundJobRunning}
                >
                  Stop Job
                </Button>
              </Grid>
              <Grid item>
                <Button
                  id="button-startJob"
                  color="primary"
                  variant="contained"
                  disabled={isRunning}
                  onClick={changeIsBackgroundJobRunning}
                >
                  Start Job
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Toolbar>
      <Grid spacing={1} container alignItems="center">
        <Grid item xs={8}>
          <AuditTrailSyncTable items={items} />
        </Grid>
      </Grid>
    </>
  );
};

export default AuditTrailSyncPage;
